import React, { useEffect, useState } from "react";
import Select from "react-select";
import axiosInstance from "../api/axiosInstance";

const RoleMaster = () => {

  const [modules, setModules] = useState([]);
  const [baseModules, setBaseModules] = useState([]);

  const [userTypes, setUserTypes] = useState([]);
  const [selectedUserType, setSelectedUserType] = useState(null);

  const [selectedRole, setSelectedRole] = useState(null);

  const [roleName, setRoleName] = useState("");
  const [loading, setLoading] = useState(false);

  /* ================= FETCH USER TYPES ================= */

  const fetchUserTypes = async () => {

    try {

      const res = await axiosInstance.get("/UserTypeMaster");

      const options = res.data.map((u) => ({
        value: u.userTypeId,
        label: u.userTypeName
      }));

      setUserTypes(options);

    } catch (err) {

      console.error("UserType fetch error", err);

    }

  };

  /* ================= FETCH MODULES ================= */

  const fetchModules = async () => {

    try {

      const res = await axiosInstance.get("/ModuleMaster/GetAll");

      const formatted = res.data.map((m) => ({
        moduleId: m.moduleId,
        moduleName: m.moduleName,
        read: false,
        write: false,
        delete: false
      }));

      setModules(formatted);
      setBaseModules(formatted);

    } catch (err) {

      console.error("Module fetch error", err);

    }

  };

  /* ================= RESET MODULE PERMISSIONS ================= */

  const resetModules = () => {

    const reset = baseModules.map((m) => ({
      ...m,
      read: false,
      write: false,
      delete: false
    }));

    setModules(reset);

  };

  /* ================= LOAD ROLE PERMISSIONS ================= */

  const loadPermissions = (role) => {

    if (!role) return;

    setRoleName(role.label);

    const updatedModules = baseModules.map((m) => {

      const access = role.accesses?.find(
        (a) => a.moduleId === m.moduleId
      );

      return {
        ...m,
        read: access?.read || false,
        write: access?.write || false,
        delete: access?.delete || false,
        isNew: !access   // ⭐ new module detection
      };

    });

    setModules(updatedModules);

  };

  /* ================= FETCH ROLE BY USER TYPE ================= */

  const fetchRoleByUserType = async (userTypeId) => {

    try {

      const res = await axiosInstance.get(
        `/RoleMaster/GetByUserType?userTypeId=${userTypeId}`
      );

      if (res.data.length > 0) {

        const role = res.data[0];

        const roleObj = {
          value: role.roleId,
          label: role.roleName,
          accesses: role.roleAccesses
        };

        setSelectedRole(roleObj);

        loadPermissions(roleObj);

      } else {

        setSelectedRole(null);
        setRoleName("");

        resetModules();

      }

    } catch (err) {

      console.error("Role fetch error", err);

    }

  };

  /* ================= PAGE LOAD ================= */

  useEffect(() => {

    fetchUserTypes();
    fetchModules();

  }, []);

  /* ================= PERMISSION TOGGLE ================= */

  const togglePermission = (index, field) => {

    const updated = [...modules];
    const module = updated[index];

    if (field === "read") {

      module.read = !module.read;

      if (!module.read) {
        module.write = false;
        module.delete = false;
      }

    }

    if (field === "write") {

      module.write = !module.write;

      if (module.write) module.read = true;
      else module.delete = false;

    }

    if (field === "delete") {

      module.delete = !module.delete;

      if (module.delete) {
        module.read = true;
        module.write = true;
      }

    }

    setModules(updated);

  };

  /* ================= SAVE ROLE ================= */

const handleSave = async () => {

  if (!roleName.trim()) {
    alert("Enter Role Name");
    return;
  }

  if (!selectedUserType) {
    alert("Select User Type");
    return;
  }

  try {

    setLoading(true);

    const roleAccesses = modules.map((m) => {

      let accessId = `${m.moduleId}N`;

      if (m.delete) accessId = `${m.moduleId}D`;
      else if (m.write) accessId = `${m.moduleId}W`;
      else if (m.read) accessId = `${m.moduleId}R`;

      return {
        moduleId: m.moduleId,
        moduleName: m.moduleName,
        accessId,
        read: m.read,
        write: m.write,
        delete: m.delete
      };

    });

    /* ================= UPDATE ROLE ================= */

    if (selectedRole) {

      await axiosInstance.put(
        `/RoleMaster/ReplaceRoleAccess?roleId=${selectedRole.value}&loginUserId=1`,
        roleAccesses
      );

      alert("Role Updated Successfully");

    }

    /* ================= CREATE ROLE ================= */

    else {

      const payload = {
        roleName: roleName,
        userTypeId: selectedUserType.value,
        loginUserId: 1,
        roleAccesses
      };

      await axiosInstance.post("RoleMaster/Create", payload);

      alert("Role Created Successfully");

    }

    fetchRoleByUserType(selectedUserType.value);

  } catch (err) {

    console.error("Save error", err);

  } finally {

    setLoading(false);

  }

};

  /* ================= UI ================= */

  return (

    <div className="max-w-5xl mx-auto bg-white p-6 rounded-xl shadow">

      <h2 className="text-xl font-bold mb-5">
        Role Permission Setup
      </h2>

      <div className="grid md:grid-cols-2 gap-4 mb-5">

        <Select
          options={userTypes}
          value={selectedUserType}
          onChange={(opt) => {

            setSelectedUserType(opt);

            setSelectedRole(null);
            setRoleName("");

            resetModules();

            fetchRoleByUserType(opt.value);

          }}
          placeholder="Select User Type"
        />

        <input
          type="text"
          placeholder="Role Name"
          value={roleName}
          onChange={(e) => setRoleName(e.target.value)}
          className="border rounded px-3 py-2"
        />

      </div>

      <table className="w-full border text-sm">

        <thead className="bg-gray-100">

          <tr>
            <th className="p-2 text-left">Module</th>
            <th className="p-2">Read</th>
            <th className="p-2">Write</th>
            <th className="p-2">Delete</th>
          </tr>

        </thead>

        <tbody>

          {modules.map((m, i) => (

            <tr
              key={m.moduleId}
              className={m.isNew ? "bg-yellow-100" : ""}
            >

              <td className="p-2">{m.moduleName}</td>

              <td className="text-center">
                <input
                  type="checkbox"
                  checked={m.read}
                  onChange={() => togglePermission(i, "read")}
                />
              </td>

              <td className="text-center">
                <input
                  type="checkbox"
                  checked={m.write}
                  onChange={() => togglePermission(i, "write")}
                />
              </td>

              <td className="text-center">
                <input
                  type="checkbox"
                  checked={m.delete}
                  onChange={() => togglePermission(i, "delete")}
                />
              </td>

            </tr>

          ))}

        </tbody>

      </table>

      <div className="mt-6 flex justify-end">

        <button
          onClick={handleSave}
          disabled={loading}
          className="bg-blue-600 text-white px-6 py-2 rounded"
        >
          {loading ? "Saving..." : "Save Role"}
        </button>

      </div>

    </div>

  );

};

export default RoleMaster;

